import { NavLink, Outlet, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import {
  LayoutDashboard, Users, FileText, DollarSign, FolderKanban,
  Settings, LogOut, ChevronLeft, ChevronRight, Menu, Briefcase, Landmark, Truck, Calendar, Wallet, GraduationCap
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useState } from 'react';

const NAV_ITEMS = [
  { to: '/dashboard', icon: LayoutDashboard, label: 'Dashboard', key: 'dashboard' },
  { to: '/leads', icon: Users, label: 'Leads', key: 'leads' },
  { to: '/bids', icon: FileText, label: 'Bids', key: 'bids' },
  { to: '/jobs', icon: Briefcase, label: 'Jobs Board', key: 'jobs' },
  { to: '/finance', icon: Landmark, label: 'Finance', key: 'finance' },
  { to: '/vendors', icon: Truck, label: 'Vendors', key: 'vendors' },
  { to: '/calendar', icon: Calendar, label: 'Calendar', key: 'calendar' },
  { to: '/payroll', icon: Wallet, label: 'Payroll', key: 'payroll' },
  { to: '/learning', icon: GraduationCap, label: 'Learning', key: 'learning' },
  { to: '/pricing-library', icon: DollarSign, label: 'Pricing Library', key: 'pricing-library' },
  { to: '/projects', icon: FolderKanban, label: 'Projects', key: 'projects' },
  { to: '/settings', icon: Settings, label: 'Settings', key: 'settings' },
];

export default function Layout() {
  const { user, logout, canSeeNav } = useAuth();
  const navigate = useNavigate();
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const visibleNav = NAV_ITEMS.filter(item => canSeeNav(item.key));
  const handleLogout = () => { logout(); navigate('/login'); };

  return (
    <div className="flex h-screen bg-[#F8F6F3] overflow-hidden">
      {mobileOpen && <div className="fixed inset-0 bg-black/30 z-30 lg:hidden" onClick={() => setMobileOpen(false)} />}

      {/* Sidebar — Deep Navy */}
      <aside data-testid="sidebar"
        className={`fixed lg:static z-40 h-full bg-[#0F1D32] flex flex-col transition-all duration-300 ${
          collapsed ? 'w-[68px]' : 'w-60'} ${mobileOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}`}>

        {/* Logo */}
        <div className={`flex items-center gap-3 px-4 h-[64px] border-b border-white/10 ${collapsed ? 'justify-center' : ''}`}>
          <img src="/hdh-logo.png" alt="HDH" className={`${collapsed ? 'w-10 h-10' : 'w-11 h-11'} object-contain`} />
          {!collapsed && (
            <div className="min-w-0">
              <h1 className="text-[13px] font-bold text-white tracking-tight truncate leading-tight" style={{fontFamily:'Manrope',fontStyle:'normal'}}>HDH Pools & Spas</h1>
              <p className="eyebrow text-[9px]" style={{color:'#F59E0B'}}>OPERATIONS</p>
            </div>
          )}
        </div>

        {/* Nav */}
        <nav className="flex-1 py-3 px-2.5 space-y-0.5 overflow-y-auto">
          {visibleNav.map(({ to, icon: Icon, label }) => (
            <NavLink key={to} to={to} onClick={() => setMobileOpen(false)}
              className={({ isActive }) => `flex items-center gap-2.5 px-3 py-2 rounded-sm text-[13px] transition-all duration-300 ${
                isActive ? 'bg-white/10 text-amber-400 font-semibold' : 'text-white/60 hover:text-amber-400 hover:bg-white/5'
              } ${collapsed ? 'justify-center px-2' : ''}`}
              data-testid={`nav-${label.toLowerCase().replace(/\s/g, '-')}`}
              style={{fontFamily:'Manrope',fontStyle:'normal'}}>
              <Icon className="w-[17px] h-[17px] flex-shrink-0" />
              {!collapsed && <span>{label}</span>}
            </NavLink>
          ))}
        </nav>

        {/* User */}
        <div className="p-3 border-t border-white/10 space-y-1.5">
          {!collapsed && user && (
            <div className="px-2 py-1">
              <p className="text-xs font-semibold text-white/80 truncate" style={{fontFamily:'Manrope',fontStyle:'normal'}}>{user.name}</p>
              <p className="text-[10px] text-amber-400 truncate font-medium" style={{fontFamily:'Manrope',fontStyle:'normal'}}>{user.role_info?.label || user.role}</p>
            </div>
          )}
          <Button variant="ghost" size="sm" onClick={handleLogout}
            className={`w-full text-white/40 hover:text-red-400 hover:bg-white/5 text-xs rounded-sm ${collapsed ? 'px-2' : ''}`}
            data-testid="logout-btn">
            <LogOut className="w-3.5 h-3.5" />{!collapsed && <span className="ml-2">Sign Out</span>}
          </Button>
          <Button variant="ghost" size="sm" onClick={() => setCollapsed(!collapsed)}
            className="w-full text-white/20 hidden lg:flex text-xs rounded-sm hover:text-white/40" data-testid="collapse-sidebar-btn">
            {collapsed ? <ChevronRight className="w-3.5 h-3.5" /> : <ChevronLeft className="w-3.5 h-3.5" />}
          </Button>
        </div>
      </aside>

      {/* Main */}
      <div className="flex-1 flex flex-col min-w-0">
        <header className="h-[52px] bg-white border-b border-slate-200 flex items-center px-4 lg:px-6 z-10 sticky top-0">
          <Button variant="ghost" size="icon" className="lg:hidden mr-2 text-slate-500 rounded-sm" onClick={() => setMobileOpen(true)} data-testid="mobile-menu-btn">
            <Menu className="w-5 h-5" />
          </Button>
          <div className="flex-1" />
          {user && (
            <div className="flex items-center gap-2.5">
              <span className="text-xs text-slate-500 hidden sm:block font-medium" style={{fontFamily:'Manrope',fontStyle:'normal'}}>{user.name}</span>
              <div className="w-8 h-8 rounded-sm bg-[#0F1D32] flex items-center justify-center">
                <span className="text-amber-400 text-xs font-bold" style={{fontFamily:'Manrope',fontStyle:'normal'}}>{user.name?.charAt(0)}</span>
              </div>
            </div>
          )}
        </header>
        <main className="flex-1 overflow-y-auto">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
